import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { useCapacitorPreferences } from './useCapacitorPreferences';

const BUNDLE_ORDER_KEY = 'liveUpdateBundleOrder';
const MAX_BUNDLES = 3; // current + 2 previous

interface LiveUpdateConfig {
  url: string;
  apiKey: string;
}

export const useCapacitorLiveUpdate = (config: LiveUpdateConfig) => {
  const { getPreference, setPreference } = useCapacitorPreferences();

  const ready = async () => {
    const result = await LiveUpdate.ready();
    if (result.rollback) {
      console.warn('LiveUpdate: rolled back to default bundle');
    }
    return result;
  };

  const sync = async () => {
    let response: Response;
    try {
      response = await fetch(config.url, {
        headers: { 'x-api-key': config.apiKey }
      });
    } catch (err) {
      console.error('LiveUpdate: network error', err);
      return;
    }

    if (!response.ok) {
      switch (response.status) {
        case 400:
          console.error('LiveUpdate: bad request (400)');
          break;
        case 401:
          console.error('LiveUpdate: unauthorized (401)');
          break;
        case 403:
          console.error('LiveUpdate: forbidden (403)');
          break;
        case 404:
          console.log('LiveUpdate: no update available (404)');
          break;
        case 405:
          console.error('LiveUpdate: method not allowed (405)');
          break;
        case 500:
          console.error('LiveUpdate: server error (500)');
          break;
        default:
          console.error(`LiveUpdate: unexpected error (${response.status})`);
      }
      return;
    }

    const { bundleId, url } = await response.json();

    const { bundleIds: downloadedIds } = await LiveUpdate.getDownloadedBundles();
    if (!downloadedIds.includes(bundleId)) {
      await LiveUpdate.downloadBundle({ url, bundleId });
    }
    await LiveUpdate.setNextBundle({ bundleId });

    const stored = await getPreference(BUNDLE_ORDER_KEY);
    let order: string[] = stored ? JSON.parse(stored) : [];

    // Prepend new bundle, deduplicate, drop any IDs no longer on device
    const knownIds = new Set([bundleId, ...downloadedIds]);
    order = [bundleId, ...order.filter(id => id !== bundleId && knownIds.has(id))];

    const toDelete = order.slice(MAX_BUNDLES);
    for (const id of toDelete) {
      await LiveUpdate.deleteBundle({ bundleId: id });
    }

    await setPreference(BUNDLE_ORDER_KEY, JSON.stringify(order.slice(0, MAX_BUNDLES)));
  };

  return { ready, sync };
};
